import {
  applyDanceHit,
  DANCE_GOOD_MS,
  DANCE_LANES,
  danceChart,
  danceRanking,
  isJudged,
  judgeDanceHit,
  newDancePlayer,
  type DanceBattleData,
  type DanceGrade,
  type DancePlayer,
} from '@veriverse/shared';
import { type Room } from 'colyseus.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendStation, useServerNow, useStationData } from '../net/game-station.js';
import { isTyping } from '../game/typing.js';

/** Keys for the four lanes: next to each other, and none of them move the avatar. */
const LANE_KEYS = ['h', 'j', 'k', 'l'] as const;
const LANE_COLORS = ['#e0707a', '#d4a017', '#7ec28b', '#7aa7e0'];
/** How long a note takes to fall from the top to the hit line. */
const FALL_MS = 1_400;
const FLUSH_MS = 300;
const WIDTH = 240;
const HEIGHT = 300;
const HIT_Y = HEIGHT - 40;

/** Dance Battle panel (spec §6.14.5): lobby, note highway while a round runs, results. */
export function DancePanel({
  room,
  objectId,
  onClose,
}: {
  room: Room;
  objectId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const data = useStationData<DanceBattleData>(room, objectId);
  const { now, ready } = useServerNow(room, objectId, 'dance_floor');
  const me = data?.players.find((p) => p.id === room.sessionId);

  // Leaving the floor (walking away, or the server releasing us) closes the panel.
  useEffect(() => {
    if (data !== undefined && !me) onClose();
  }, [data, me, onClose]);

  const leave = () => {
    sendStation(room, objectId, 'leave');
    onClose();
  };

  return (
    <div className="hud-box game-panel" role="dialog" aria-label={t('games.dance.title')}>
      <div className="game-panel-header">
        <strong>{t('games.dance.title')}</strong>
        <button type="button" className="secondary" onClick={leave}>
          {t('games.leave')}
        </button>
      </div>
      {data?.phase === 'playing' && me ? (
        <Highway room={room} objectId={objectId} data={data} now={now} ready={ready} />
      ) : (
        <>
          <p className="notice">
            {data?.phase === 'results' ? t('games.dance.results') : t('games.dance.help')}
          </p>
          <Scoreboard players={data?.players ?? []} />
          {data?.phase !== 'results' && (
            <button
              type="button"
              disabled={!ready}
              onClick={() => sendStation(room, objectId, 'start')}
            >
              {t('games.dance.start')}
            </button>
          )}
          <p className="notice">
            {t('games.dance.keys', { keys: LANE_KEYS.join(' ').toUpperCase() })}
          </p>
        </>
      )}
    </div>
  );
}

function Scoreboard({ players }: { players: readonly DancePlayer[] }) {
  const { t } = useTranslation();
  const ranked = danceRanking(players);
  return (
    <ol className="game-scores">
      {ranked.map((p) => (
        <li key={p.id}>
          <span>{p.name}</span>
          <span>
            {p.score} · {t('games.dance.bestCombo', { count: p.best })}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Highway({
  room,
  objectId,
  data,
  now,
  ready,
}: {
  room: Room;
  objectId: number;
  data: DanceBattleData;
  now: () => number;
  ready: boolean;
}) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);
  const chart = useMemo(() => danceChart(data.seed), [data.seed]);
  // The local copy of my round: instant feedback; the server's score is the one that counts.
  const local = useRef<DancePlayer>(newDancePlayer('me', ''));
  const pending = useRef<{ lane: number; t: number }[]>([]);
  const [feedback, setFeedback] = useState<
    { grade: DanceGrade | 'miss'; at: number } | undefined
  >();
  const [, setTick] = useState(0);

  useEffect(() => {
    local.current = newDancePlayer('me', '');
    pending.current = [];
  }, [data.seed, data.startAt]);

  // Presses: judge locally, queue for the server.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || isTyping() || event.ctrlKey || event.metaKey || event.altKey) return;
      const lane = LANE_KEYS.indexOf(event.key.toLowerCase() as (typeof LANE_KEYS)[number]);
      if (lane < 0) return;
      const time = now();
      pending.current.push({ lane, t: time });
      const judgement = judgeDanceHit(chart, local.current.judged, lane, time - data.startAt);
      local.current = applyDanceHit(chart, local.current, judgement);
      setFeedback({ grade: judgement?.grade ?? 'miss', at: performance.now() });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chart, data.startAt, now]);

  // Send the queued presses in batches.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (pending.current.length === 0) return;
      const hits = pending.current.splice(0, 16);
      sendStation(room, objectId, 'hit', { hits });
    }, FLUSH_MS);
    return () => window.clearInterval(timer);
  }, [room, objectId]);

  // Draw: notes fall towards the hit line. Nothing flashes, so reduce-motion needs no variant.
  useEffect(() => {
    let frame = 0;
    const draw = () => {
      const ctx = canvas.current?.getContext('2d');
      if (ctx) {
        const songTime = now() - data.startAt;
        const laneWidth = WIDTH / DANCE_LANES;
        ctx.fillStyle = '#14161e';
        ctx.fillRect(0, 0, WIDTH, HEIGHT);
        for (let lane = 0; lane < DANCE_LANES; lane++) {
          ctx.fillStyle = lane % 2 ? '#1a1e2a' : '#171a24';
          ctx.fillRect(lane * laneWidth, 0, laneWidth, HEIGHT);
        }
        ctx.fillStyle = '#eceff4';
        ctx.fillRect(0, HIT_Y, WIDTH, 2);
        for (const note of chart) {
          const dt = note.t - songTime;
          if (dt > FALL_MS || dt < -DANCE_GOOD_MS * 2) continue;
          if (isJudged(local.current.judged, note.index)) continue;
          const y = HIT_Y - (dt / FALL_MS) * HIT_Y;
          ctx.fillStyle = LANE_COLORS[note.lane] ?? '#fff';
          ctx.fillRect(note.lane * laneWidth + 6, y - 8, laneWidth - 12, 16);
        }
        ctx.font = '12px monospace';
        ctx.fillStyle = '#a3acbd';
        for (let lane = 0; lane < DANCE_LANES; lane++) {
          ctx.fillText(
            (LANE_KEYS[lane] ?? '').toUpperCase(),
            lane * laneWidth + laneWidth / 2 - 4,
            HIT_Y + 22,
          );
        }
        if (songTime < 0) {
          ctx.fillStyle = '#d4a017';
          ctx.font = '28px monospace';
          ctx.fillText(String(Math.ceil(-songTime / 1000)), WIDTH / 2 - 8, HEIGHT / 2);
        }
      }
      setTick((n) => (n + 1) % 1_000_000);
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frame);
  }, [chart, data.startAt, now]);

  const mine = local.current;
  const recent = feedback && performance.now() - feedback.at < 600 ? feedback.grade : undefined;
  return (
    <div className="dance-highway">
      <canvas ref={canvas} width={WIDTH} height={HEIGHT} aria-label={t('games.dance.highway')} />
      <div className="dance-hud" aria-live="off">
        <span>{t('games.dance.combo', { count: mine.combo })}</span>
        <span>{recent ? t(`games.dance.grade.${recent}`) : ' '}</span>
        {!ready && <span>{t('games.syncing')}</span>}
      </div>
      <Scoreboard players={data.players} />
    </div>
  );
}
